import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';

import { Button } from '@/components/atoms/Button';
import { Checkbox } from '@/components/atoms/Checkbox/Checkbox';
import { Text } from '@/components/atoms/Text';
import { Input } from '@/components/atoms/Form/Input';
import PasswordInput from '@/components/atoms/Form/PasswordInput/PasswordInput';
import ErrorMessage from '@/components/atoms/ErrorMessage';
import { Routes } from '@/core/router/routes.types';
import {
    Center,
    ExtraRow,
    ForgotPasswordLink,
    FormContainer,
    FormTitle,
    InputSpacer,
    MarginTop,
} from './LoginPanel.style';

type LoginPanelProps = {
    email: string;
    password: string;
    onEmailChange: (email: string) => void;
    onPasswordChange: (password: string) => void;
    samlEnabled: boolean;
    samlLoading: boolean;
    onSubmit: (payload: { email: string; password: string; rememberMe: boolean }) => void;
    errorMessage: React.ReactNode;
    captchaRequired?: boolean;
    captchaSiteKey?: string;
    captchaRef?: React.RefObject<ReCAPTCHA>;
    captchaToken?: string | null;
    onCaptchaChange?: (token: string | null) => void;
    lockoutSecondsLeft?: number | null;
    ipNotAllowed?: boolean;
};

const LoginPanel: React.FunctionComponent<React.PropsWithChildren<LoginPanelProps>> = ({
    email,
    password,
    onEmailChange,
    onPasswordChange,
    samlEnabled,
    samlLoading,
    onSubmit,
    errorMessage,
    captchaRequired = false,
    captchaSiteKey,
    captchaRef,
    captchaToken,
    onCaptchaChange,
    lockoutSecondsLeft = null,
    ipNotAllowed = false,
}) => {
    const intl = useIntl();
    const [rememberMe, setRememberMe] = useState(false);

    const submit = () => {
        onSubmit({
            email,
            password,
            rememberMe,
        });
    };

    const isLockedOut = lockoutSecondsLeft !== null && lockoutSecondsLeft > 0;
    const captchaBlocking = captchaRequired && !captchaToken;
    const captchaConfigured = Boolean(captchaSiteKey && captchaSiteKey.length > 0);

    return (
        <div>
            <Center>
                <FormContainer onSubmit={(e) => e.preventDefault()}>
                    <FormTitle>
                        <FormattedMessage id="login.panelTitle" />
                    </FormTitle>
                    <InputSpacer>
                        <Input
                            type="text"
                            onChange={(event): unknown => onEmailChange(event.target.value)}
                            placeholder="Email"
                            value={email}
                        />
                    </InputSpacer>
                    {samlLoading ? (
                        <InputSpacer>
                            <Text text="login.ssoChecking" />
                        </InputSpacer>
                    ) : null}
                    {samlEnabled ? (
                        <InputSpacer>
                            <Text text="login.ssoEnabled" />
                        </InputSpacer>
                    ) : (
                        <PasswordInput value={password} onChange={(value) => onPasswordChange(value)} />
                    )}

                    {!samlEnabled && (
                        <ExtraRow>
                            <Checkbox
                                checked={rememberMe}
                                onChange={(event): void => setRememberMe((event.target as HTMLInputElement).checked)}
                                label={intl.formatMessage({ id: 'login.rememberMe' })}
                            />
                            <ForgotPasswordLink to={Routes.ForgotPassword}>
                                <FormattedMessage id="login.forgotPassword" />
                            </ForgotPasswordLink>
                        </ExtraRow>
                    )}

                    {!samlEnabled && captchaRequired && captchaConfigured && captchaSiteKey && onCaptchaChange ? (
                        <InputSpacer>
                            <ReCAPTCHA ref={captchaRef} sitekey={captchaSiteKey} onChange={onCaptchaChange} />
                        </InputSpacer>
                    ) : null}

                    {errorMessage ? <ErrorMessage>{errorMessage}</ErrorMessage> : null}
                    <MarginTop>
                        <Button
                            onClick={submit}
                            buttonType="primary"
                            fullWidth
                            thick
                            disabled={
                                samlLoading ||
                                !email ||
                                (!samlEnabled && !password) ||
                                captchaBlocking ||
                                isLockedOut ||
                                ipNotAllowed
                            }
                        >
                            <Text text={samlEnabled ? 'login.ssoLogin' : 'login.login'} />
                        </Button>
                    </MarginTop>
                </FormContainer>
            </Center>
        </div>
    );
};

export default LoginPanel;
